import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/apiClient";
import type { ConsumableRollSummary, InventoryBalance, PaperRollRow } from "../../api/types";

export interface ConsumableItemPanelProps {
  locationId: string;
  item: InventoryBalance["item"];
  onChanged: () => void;
  bouquetBuildId?: string | null;
}

/** G-175 (KI-154): why a roll has no cost per bouquet — never one "—" for three reasons. */
const COST_PER_BOUQUET_MISSING: Record<NonNullable<ConsumableRollSummary["costPerBouquetMissing"]>, string> = {
  NO_BOUQUETS: "no bouquets yet",
  NO_PRICE: "roll has no price",
  SPLIT_PART: "priced on the roll it was split from",
};

function eur(s: string | null | undefined): string {
  if (s === null || s === undefined) return "—";
  const n = Number(s);
  return Number.isFinite(n) ? `€${n.toFixed(2)}` : "—";
}

export function ConsumableItemPanel({
  locationId,
  item,
  onChanged,
  bouquetBuildId = null,
}: ConsumableItemPanelProps) {
  const [rolls, setRolls] = useState<PaperRollRow[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [rollSummary, setRollSummary] = useState<ConsumableRollSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [customCm, setCustomCm] = useState("40");
  const [finishNotes, setFinishNotes] = useState("");

  const refresh = useCallback(async () => {
    setErr(null);
    try {
      const list = await api.listPaperRolls(locationId);
      const forItem = list.filter((r) => r.item.id === item.id);
      setRolls(forItem);
      setSelectedBatchId((prev) => {
        if (prev && forItem.some((r) => r.batchId === prev)) return prev;
        return forItem[0]?.batchId ?? "";
      });
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Load failed");
    }
  }, [item.id, locationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selected = rolls.find((r) => r.batchId === selectedBatchId) ?? null;

  useEffect(() => {
    if (!selectedBatchId) {
      setRollSummary(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const sum = await api.getConsumableRollSummary(selectedBatchId);
        if (!cancelled) setRollSummary(sum);
      } catch {
        if (!cancelled) setRollSummary(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBatchId]);

  const consume = async (sizePreset?: "SMALL" | "MEDIUM" | "BIG", custom?: number) => {
    if (!selectedBatchId) return;
    setBusy(true);
    setErr(null);
    try {
      await api.consumeConsumable({
        locationId,
        itemId: item.id,
        batchId: selectedBatchId,
        ...(bouquetBuildId ? { bouquetBuildId } : {}),
        ...(sizePreset ? { sizePreset } : { customCm: custom }),
      });
      await refresh();
      onChanged();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Could not consume");
    } finally {
      setBusy(false);
    }
  };

  const finishRoll = async () => {
    if (!selectedBatchId) return;
    setBusy(true);
    setErr(null);
    try {
      await api.finishConsumableRoll({
        locationId,
        batchId: selectedBatchId,
        ...(finishNotes.trim() ? { notes: finishNotes.trim() } : {}),
      });
      setFinishNotes("");
      await refresh();
      onChanged();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Could not end roll");
    } finally {
      setBusy(false);
    }
  };

  const canConsume = !!selected && Number(selected.remainingQty) > 0;

  return (
    <div
      style={{
        padding: "var(--space-4)",
        borderRadius: "var(--radius-md)",
        background: "var(--color-surface-muted)",
        boxShadow: "var(--shadow-1)",
      }}
    >
      <h3 style={{ margin: "0 0 var(--space-2)" }}>{item.name}</h3>
      {err ? <div className="alert error">{err}</div> : null}
      {bouquetBuildId ? (
        <p className="muted" style={{ margin: "0 0 var(--space-2)", fontSize: 13 }}>
          Uses are attributed to this bouquet build for roll efficiency stats.
        </p>
      ) : null}

      <div className="field" style={{ marginBottom: "var(--space-3)" }}>
        <label htmlFor={`roll-${item.id}`}>Batch (any zone)</label>
        <select
          id={`roll-${item.id}`}
          value={selectedBatchId}
          onChange={(e) => setSelectedBatchId(e.target.value)}
          disabled={busy || rolls.length === 0}
        >
          {rolls.length === 0 ? (
            <option value="">No rolls in stock</option>
          ) : (
            rolls.map((r) => (
              <option key={r.batchId} value={r.batchId}>
                {r.batchCode} — {r.remainingQty} cm · {r.zoneName}
              </option>
            ))
          )}
        </select>
      </div>

      {selected && rollSummary ? (
        <div
          style={{
            marginBottom: "var(--space-3)",
            padding: "var(--space-3)",
            borderRadius: "var(--radius-md)",
            background: "var(--color-surface)",
            fontSize: 13,
          }}
        >
          <div className="muted" style={{ textTransform: "uppercase", fontSize: 11, marginBottom: 8 }}>
            Roll efficiency (this batch)
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-4)" }}>
            <span>
              <strong>{rollSummary.bouquetsAttributedCount}</strong> bouquet
              {rollSummary.bouquetsAttributedCount === 1 ? "" : "s"} with logged paper/ribbon
            </span>
            <span>
              Cost / attributed bouquet:{" "}
              {rollSummary.costPerBouquetMissing === null ? (
                <>
                  <strong>{eur(rollSummary.costPerBouquetEur)}</strong> (
                  {eur(rollSummary.invoiceTotalEur)} roll invoice ÷ tickets)
                </>
              ) : (
                <strong>{COST_PER_BOUQUET_MISSING[rollSummary.costPerBouquetMissing]}</strong>
              )}
            </span>
            <span className="muted">Total cm moved: {rollSummary.totalCmConsumedOnBatch} cm</span>
          </div>
        </div>
      ) : null}

      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)", alignItems: "center" }}>
        <button
          type="button"
          className="button plain"
          disabled={busy || !canConsume}
          title="Approx. 20–30 cm wrap · deducts 25 cm"
          onClick={() => void consume("SMALL")}
        >
          Small (20–30 cm → 25 cm)
        </button>
        <button
          type="button"
          className="button plain"
          disabled={busy || !canConsume}
          title="Approx. 30–70 cm wrap · deducts 50 cm"
          onClick={() => void consume("MEDIUM")}
        >
          Medium (30–70 cm → 50 cm)
        </button>
        <button
          type="button"
          className="button plain"
          disabled={busy || !canConsume}
          title="Over ~70 cm wrap · deducts 85 cm"
          onClick={() => void consume("BIG")}
        >
          Big ({">"}70 cm → 85 cm)
        </button>
        <span style={{ display: "inline-flex", gap: "var(--space-2)", alignItems: "center" }}>
          <input
            type="number"
            min={0.001}
            step="0.1"
            value={customCm}
            onChange={(e) => setCustomCm(e.target.value)}
            style={{ width: 80 }}
            aria-label="Custom cm"
          />
          <button
            type="button"
            className="button"
            disabled={busy || !canConsume}
            onClick={() => {
              const n = Number(customCm);
              if (Number.isFinite(n) && n > 0) void consume(undefined, n);
            }}
          >
            Custom cm
          </button>
        </span>
      </div>

      {selectedBatchId ? (
        <div style={{ marginTop: "var(--space-4)" }}>
          <div className="field full">
            <label htmlFor={`finish-notes-${item.id}`}>End roll — optional note</label>
            <input
              id={`finish-notes-${item.id}`}
              type="text"
              maxLength={500}
              value={finishNotes}
              onChange={(e) => setFinishNotes(e.target.value)}
              placeholder="e.g. roll finished early, switching to brown paper"
            />
            <small className="hint">
              Writes off remaining length on this batch and records it for owners.
            </small>
          </div>
          <button
            type="button"
            className="button danger"
            disabled={busy || !selected}
            onClick={() => void finishRoll()}
          >
            Roll finished — write off remainder
          </button>
        </div>
      ) : null}
    </div>
  );
}
